import { PlaidEntryPointInput, PlaidProduct } from '@varo/graphql-schema-types';
import type {
  LinkAccount,
  LinkEventMetadata,
  LinkExit,
  LinkSuccess,
} from 'react-native-plaid-link-sdk';
import type {
  PlaidAccount,
  PlaidLinkError,
  PlaidLinkOnEventMetadata,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccessMetadata,
} from 'react-plaid-link';

export enum PlaidIntent {
  Link = 'link',
  ReLink = 'relink',
  CraUpgrade = 'craUpgrade',
  Wait = 'wait',
}
export type PlaidLinkIntent = {
  intent: PlaidIntent.Link;
  product: PlaidProduct;
  entryPoint: PlaidEntryPointInput | undefined;
};

export type PlaidReLinkIntent = {
  intent: PlaidIntent.ReLink;
  accountId: string;
};

export type PlaidCraUpgradeIntent = {
  intent: PlaidIntent.CraUpgrade;
  accountId: string;
};

export type PlaidWait = { intent: PlaidIntent.Wait };

export type PlaidLaunchIntent =
  PlaidLinkIntent | PlaidReLinkIntent | PlaidCraUpgradeIntent;

export type PlaidIntentTypes = PlaidLaunchIntent | PlaidWait;

type PlaidBlockedReason = 'missing-token' | 'sdk-not-ready';

export type PlaidOpenResult =
  | { status: 'success'; success: LinkSuccess }
  | { status: 'exit'; exit: LinkExit }
  | { status: 'blocked'; reason: PlaidBlockedReason }
  | { status: 'error'; error: PlaidCallbackError };

export type PlaidSuccessMetadata = {
  linkSessionId?: string;
  institutionId?: string;
  institutionName?: string;
  accounts?: Array<PlaidAccount | LinkAccount>;
};

export type PlaidSuccessContext = {
  publicToken: string;
  metadata: PlaidSuccessMetadata;
  sdkVersion: string;
};

export type PlaidExitMetadata = {
  linkSessionId?: string;
  requestId?: string;
  institutionId?: string;
  institutionName?: string;
  exitStatus?: string;
};

export type PlaidExitContext = {
  metadata?: PlaidExitMetadata;
  errorCode?: string;
  errorType?: string;
  errorMessage?: string;
  displayMessage?: string;
};

export type PlaidCallbackError = {
  name: string;
  message: string;
};

export type PlaidV2SharedCallbacks = {
  onSuccess: (
    context: PlaidSuccessContext,
  ) => Promise<PlaidCallbackError | 'success'>;
  onExit: (context: PlaidExitContext) => PlaidCallbackError | void;
  onEvent: (
    eventName: string,
    metadata: LinkEventMetadata | PlaidLinkOnEventMetadata,
  ) => void;
};

type WebOpenSuccess = {
  publicToken: string;
  metadata: PlaidLinkOnSuccessMetadata;
};

type WebOpenExit = {
  error: PlaidLinkError | null;
  metadata: PlaidLinkOnExitMetadata;
};

export type WebOnResolve =
  | { status: 'success'; success: WebOpenSuccess }
  | { status: 'exit'; exit: WebOpenExit }
  | {
      status: 'error';
      error: PlaidLinkError | { name: string; message: string };
    }
  | { status: 'blocked'; reason: 'missing-token' | 'sdk-not-ready' };
